import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { COUNT_CAP } from '@/shared/utils/countCap';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  IconButton,
  Badge,
  Menu,
  Typography,
  Box,
  Divider,
  Button,
  CircularProgress,
  FormControlLabel,
  Switch,
  ButtonBase,
} from '@mui/material';
import {
  Notifications as NotificationsIcon,
  NotificationsNone as NotificationsNoneIcon,
  Event as EventIcon,
  Person as PersonIcon,
  Inventory as InventoryIcon,
  Receipt as ReceiptIcon,
  MedicalServices as TreatmentIcon,
  Settings as SystemIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { toast } from '@/shared/feedback/toast';
import {
  getNotifications,
  getUnreadCount,
  markAsRead,
  markAllAsRead,
  type AppNotification,
} from '@/modules/notification/api/notification.api';
import { useTimezone } from '@/shared/context/TimezoneContext';
import { queryKeys } from '@/shared/query';
import { elevation } from '@/shared/theme/tokens';
import { type Theme } from '@mui/material/styles';

const BELL_LIMIT = 15;

/** Left accent — same palette as priority chips on the notifications page. */
const getPriorityAccent = (theme: Theme, priority: string): string => {
  switch (priority) {
    case 'urgent':
      return theme.palette.error.main;
    case 'high':
      return theme.palette.warning.main;
    case 'medium':
      return theme.palette.info.main;
    case 'low':
      return theme.palette.success.main;
    default:
      return theme.palette.grey[400];
  }
};

const NotificationBell: React.FC = () => {
  const { t } = useTranslation(['notification', 'common']);
  const { formatOrgDate } = useTimezone();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [onlyUnread, setOnlyUnread] = useState(true);
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const open = Boolean(anchorEl);

  const listParams = useMemo(
    () => ({
      page: 1,
      limit: BELL_LIMIT,
      ...(onlyUnread ? { read: false as const } : {}),
    }),
    [onlyUnread]
  );

  const notificationsQuery = useQuery({
    queryKey: queryKeys.notification.list(listParams),
    queryFn: () => getNotifications(listParams),
  });

  const unreadQuery = useQuery({
    queryKey: queryKeys.notification.unread,
    queryFn: () => getUnreadCount(),
  });

  const notifications = notificationsQuery.data?.data ?? [];
  const unreadCount = unreadQuery.data?.count ?? 0;
  const loading = notificationsQuery.isPending || unreadQuery.isPending;

  useEffect(() => {
    const onRefresh = (): void => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.notification.all });
    };
    window.addEventListener('notifications:refresh', onRefresh);
    return () => {
      window.removeEventListener('notifications:refresh', onRefresh);
    };
  }, [queryClient]);

  const handleClick = (event: React.MouseEvent<HTMLElement>): void => {
    setAnchorEl(event.currentTarget);
    void queryClient.invalidateQueries({ queryKey: queryKeys.notification.all }).catch(() => {
      toast.error(t('toasts.loadFailed'));
    });
  };

  const handleClose = (): void => {
    setAnchorEl(null);
  };

  const markReadMutation = useMutation({
    mutationFn: (notificationId: string) => markAsRead(notificationId),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: queryKeys.notification.all }),
    onError: (error) => {
      console.error('Error handling notification click:', error);
      toast.error(t('toasts.markReadFailed'));
    },
  });

  const markAllReadMutation = useMutation({
    mutationFn: () => markAllAsRead(),
    onSuccess: async () => {
      toast.success(t('toasts.markAllSuccess'));
      await queryClient.invalidateQueries({ queryKey: queryKeys.notification.all });
    },
    onError: (error) => {
      console.error('Error marking all as read:', error);
      toast.error(t('toasts.markAllFailed'));
    },
  });

  const handleNotificationClick = (notification: AppNotification): void => {
    if (!notification.isRead) {
      markReadMutation.mutate(notification._id);
    }
  };

  const handleOpenNotification = (
    event: React.MouseEvent,
    notification: AppNotification
  ): void => {
    event.preventDefault();
    event.stopPropagation();

    if (!notification.isRead) {
      markReadMutation.mutate(notification._id);
    }

    if (notification.actionUrl) {
      navigate(notification.actionUrl);
      handleClose();
    }
  };

  const handleMarkAllAsRead = (): void => {
    markAllReadMutation.mutate();
  };

  const handleOpenCenter = (): void => {
    navigate('/notifications');
    handleClose();
  };

  const getNotificationIcon = (type: string): React.ReactNode => {
    switch (type) {
      case 'appointment':
      case 'reminder':
        return <EventIcon fontSize="small" color="primary" />;
      case 'patient':
        return <PersonIcon fontSize="small" color="success" />;
      case 'inventory':
        return <InventoryIcon fontSize="small" color="warning" />;
      case 'payment':
        return <ReceiptIcon fontSize="small" color="error" />;
      case 'treatment':
        return <TreatmentIcon fontSize="small" color="info" />;
      case 'system':
        return <SystemIcon fontSize="small" color="action" />;
      default:
        return <NotificationsIcon fontSize="small" />;
    }
  };

  const formatTime = (dateString: string): string => {
    const date = new Date(dateString);
    const now = new Date();
    const diffInHours = (now.getTime() - date.getTime()) / (1000 * 60 * 60);

    if (diffInHours < 1 / 60) {
      return t('bell.justNow');
    }
    if (diffInHours < 1) {
      return t('bell.minutesAgo', { count: Math.max(1, Math.floor(diffInHours * 60)) });
    }
    if (diffInHours < 24) {
      return t('bell.hoursAgo', { count: Math.floor(diffInHours) });
    }
    if (diffInHours < 24 * 7) {
      return t('bell.daysAgo', { count: Math.floor(diffInHours / 24) });
    }
    return formatOrgDate(dateString, 'date') || '';
  };

  return (
    <>
      <IconButton
        color="inherit"
        onClick={handleClick}
        aria-label={t('bell.title')}
        aria-controls={open ? 'notification-menu' : undefined}
        aria-haspopup="true"
        aria-expanded={open ? 'true' : undefined}
      >
        <Badge badgeContent={unreadCount} max={COUNT_CAP} color="error">
          <NotificationsIcon />
        </Badge>
      </IconButton>

      <Menu
        id="notification-menu"
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
        transformOrigin={{ horizontal: 'right', vertical: 'top' }}
        slotProps={{
          paper: {
            elevation: elevation.menu,
            sx: {
              width: 400,
              maxWidth: 'calc(100vw - 24px)',
              overflow: 'hidden',
              mt: 1.25,
              border: 1,
              borderColor: 'divider',
            },
          },
          list: {
            sx: {
              p: 0,
              maxWidth: '100%',
            },
          },
        }}
      >
        <Box
          sx={{
            px: 2,
            py: 1.25,
            borderBottom: 1,
            borderColor: 'divider',
          }}
        >
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              gap: 1.5,
              minWidth: 0,
            }}
          >
            <Typography variant="subtitle1" fontWeight={600} noWrap>
              {t('bell.title')}
            </Typography>
            <FormControlLabel
              sx={{
                mr: 0,
                flexShrink: 0,
                gap: 0.5,
                '& .MuiFormControlLabel-label': {
                  typography: 'body2',
                  color: 'text.secondary',
                  userSelect: 'none',
                },
              }}
              control={
                <Switch
                  size="small"
                  checked={onlyUnread}
                  onClick={(e) => e.stopPropagation()}
                  onChange={(_, checked) => setOnlyUnread(checked)}
                  inputProps={{ 'aria-label': t('bell.onlyUnread') }}
                />
              }
              label={t('bell.onlyUnread')}
              onClick={(e) => e.stopPropagation()}
            />
          </Box>
        </Box>

        <Box
          sx={{
            maxHeight: 'min(480px, calc(100vh - 220px))',
            overflowY: 'auto',
            overflowX: 'hidden',
          }}
        >
          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress size={24} />
            </Box>
          ) : notifications.length === 0 ? (
            <Box sx={{ px: 2, py: 4, textAlign: 'center' }}>
              <NotificationsNoneIcon
                sx={{ fontSize: 36, color: 'text.disabled', mb: 1 }}
              />
              <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 0.5 }}>
                {onlyUnread ? t('bell.emptyUnreadTitle') : t('bell.emptyTitle')}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: onlyUnread ? 1.5 : 0 }}>
                {onlyUnread ? t('bell.emptyUnread') : t('bell.empty')}
              </Typography>
              {onlyUnread ? (
                <Button
                  size="small"
                  onClick={(e) => {
                    e.stopPropagation();
                    setOnlyUnread(false);
                  }}
                >
                  {t('bell.showAll')}
                </Button>
              ) : null}
            </Box>
          ) : (
            notifications.map((notification, index) => (
              <Box
                key={notification._id}
                sx={{
                  display: 'flex',
                  alignItems: 'stretch',
                  borderBottom:
                    index < notifications.length - 1 ? 1 : 0,
                  borderColor: 'divider',
                  borderLeft: 3,
                  borderLeftColor: (theme) =>
                    getPriorityAccent(theme, String(notification.priority)),
                  bgcolor: notification.isRead ? 'transparent' : 'action.hover',
                  opacity: notification.isRead ? 0.78 : 1,
                  '&:hover': {
                    bgcolor: 'action.selected',
                    opacity: 1,
                  },
                }}
              >
                <ButtonBase
                  onClick={() => handleNotificationClick(notification)}
                  focusRipple
                  sx={{
                    display: 'flex',
                    alignItems: 'flex-start',
                    gap: 1.25,
                    flex: 1,
                    minWidth: 0,
                    textAlign: 'left',
                    px: 1.75,
                    py: 1.25,
                    boxSizing: 'border-box',
                  }}
                >
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      width: 28,
                      height: 28,
                      flexShrink: 0,
                      mt: 0.125,
                    }}
                  >
                    {getNotificationIcon(notification.type)}
                  </Box>

                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography
                      variant="body2"
                      sx={{
                        fontWeight: notification.isRead ? 500 : 600,
                        overflowWrap: 'anywhere',
                        wordBreak: 'break-word',
                        mb: 0.25,
                      }}
                    >
                      {notification.title}
                    </Typography>

                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{
                        overflowWrap: 'anywhere',
                        wordBreak: 'break-word',
                        whiteSpace: 'pre-wrap',
                        mb: 0.5,
                        lineHeight: 1.4,
                      }}
                    >
                      {notification.message}
                    </Typography>

                    <Typography variant="caption" color="text.secondary">
                      {formatTime(notification.createdAt)}
                    </Typography>
                  </Box>
                </ButtonBase>

                {notification.actionUrl ? (
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      pr: 1.25,
                      flexShrink: 0,
                    }}
                  >
                    <Button
                      size="small"
                      onClick={(e) => handleOpenNotification(e, notification)}
                    >
                      {t('actions.open')}
                    </Button>
                  </Box>
                ) : null}
              </Box>
            ))
          )}
        </Box>

        <Divider />
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 1,
            px: 1.5,
            py: 1,
          }}
        >
          <Button
            size="small"
            disabled={unreadCount === 0 || markAllReadMutation.isPending}
            onClick={handleMarkAllAsRead}
            sx={{ flexShrink: 0 }}
          >
            {t('actions.markAllRead')}
          </Button>
          <Button
            size="small"
            onClick={handleOpenCenter}
            sx={{ flexShrink: 0 }}
          >
            {t('bell.goToCenter')}
          </Button>
        </Box>
      </Menu>
    </>
  );
};

export default NotificationBell;
